import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocationParams } from 'hooks';
import { useCampaigns } from '../api';
import { buildCampaignFetchParams } from '../utils/campaignFetchParams';
import { restoreSelectedCampaignsFromUrl } from '../utils/campaignUrlSync';
import {
  isDeepLinkStepPass,
  resolveFetchedStepStatus,
} from '../utils/deepLinkInit';
import type {
  DeepLinkDependentStepGate,
  DeepLinkStepResult,
} from './deepLinkTypes';

const useDeepLinkCampaignsStep = ({
  enabled,
  previousPass,
}: DeepLinkDependentStepGate): DeepLinkStepResult => {
  const { setValue } = useFormContext();
  const {
    marketingProcess: marketingProcessParam,
    adAccounts: adAccountsParam,
    selectedCampaigns: selectedCampaignsParam,
  } = useLocationParams();

  const campaignsQuery = useCampaigns({
    params: buildCampaignFetchParams(marketingProcessParam ?? undefined, [
      ...adAccountsParam,
    ]),
    config: {
      enabled: enabled && previousPass && adAccountsParam.length > 0,
    },
  });

  const missingEntries = useMemo(
    () =>
      campaignsQuery.data
        ? selectedCampaignsParam.filter(
            (campaign) =>
              !campaignsQuery.data!.some(
                (row) => String(row.id) === campaign.id
              )
          )
        : [],
    [campaignsQuery.data, selectedCampaignsParam]
  );

  const missing = useMemo(
    () => missingEntries.map((campaign) => campaign.id),
    [missingEntries]
  );

  const status = resolveFetchedStepStatus({
    enabled,
    previousPass,
    isPending: campaignsQuery.isPending,
    isError: campaignsQuery.isError,
    hasMissing: missingEntries.length > 0,
  });

  const restore = useCallback(() => {
    if (!enabled || !campaignsQuery.data) return;

    const { selectedCampaigns, selectedCampaignsBudgets } =
      restoreSelectedCampaignsFromUrl(
        selectedCampaignsParam,
        campaignsQuery.data
      );
    setValue('selectedCampaigns', selectedCampaigns);
    setValue('selectedCampaignsBudgets', selectedCampaignsBudgets);
  }, [campaignsQuery.data, enabled, selectedCampaignsParam, setValue]);

  return {
    status,
    pass: isDeepLinkStepPass(enabled, status),
    missing,
    restore,
  };
};

export default useDeepLinkCampaignsStep;
